import { supabase } from './supabaseClient';
import { expiryDate } from './lab';

/* Data access for the lab: product types, methods, variants and batches.
 *
 * The arithmetic lives in lab.js and is tested there; this file only moves
 * rows. The one rule it enforces is the important one — a batch stores what
 * it was made from rather than pointing at something that can change. */

// ------------------------------------------------------------ product types

export async function listProductTypes() {
  const { data, error } = await supabase
    .from('product_types')
    .select('*, default_method:default_method_id(id, name)')
    .order('sort_order');
  if (error) throw error;
  return data;
}

export async function updateProductType(id, updates) {
  const { data, error } = await supabase
    .from('product_types')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function addProductType(fields) {
  const { data, error } = await supabase.from('product_types').insert(fields).select().single();
  if (error) throw error;
  return data;
}

// ------------------------------------------------------------------ methods

export async function listMethods() {
  const { data, error } = await supabase
    .from('methods')
    .select('*, product_type:product_type_id(id, name)')
    .order('name');
  if (error) throw error;
  return data;
}

export async function addMethod(fields) {
  const { data, error } = await supabase.from('methods').insert(fields).select().single();
  if (error) throw error;
  return data;
}

export async function updateMethod(id, updates) {
  const { data, error } = await supabase
    .from('methods')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMethod(id) {
  const { error } = await supabase.from('methods').delete().eq('id', id);
  if (error) throw error;
}

// ----------------------------------------------------------------- variants

export async function listVariants(formulaId) {
  const { data, error } = await supabase
    .from('formula_variants')
    .select('*')
    .eq('formula_id', formulaId)
    .order('name');
  if (error) throw error;
  return data;
}

export async function addVariant(fields) {
  const { data, error } = await supabase.from('formula_variants').insert(fields).select().single();
  if (error) throw error;
  return data;
}

export async function updateVariant(id, updates) {
  const { data, error } = await supabase
    .from('formula_variants')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteVariant(id) {
  const { error } = await supabase.from('formula_variants').delete().eq('id', id);
  if (error) throw error;
}

// ------------------------------------------------------------------ batches

const BATCH_SELECT = `
  *,
  variant:variant_id(id, name),
  formula:formulas!formula_batches_formula_id_fkey(
    id, version_number, version_label, family_id,
    family:formula_families(id, name, product_type_id,
      product_type:product_types(id, name, sku_code, shelf_life_months))
  )
`;

export async function listBatches({ status } = {}) {
  let query = supabase.from('formula_batches').select(BATCH_SELECT);
  if (Array.isArray(status)) query = query.in('status', status);
  else if (status) query = query.eq('status', status);
  const { data, error } = await query
    .order('date_completed', { ascending: false, nullsFirst: true })
    .order('made_on', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getBatch(id) {
  const { data, error } = await supabase
    .from('formula_batches')
    .select(BATCH_SELECT)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

/* Start a batch.
 *
 * resolved_lines and method_snapshot are written here and never read back
 * from the formula again: a batch made in August has to keep showing what it
 * actually used after the formula moves to v3. That's the whole point of the
 * snapshot, and it's why this function takes them as arguments rather than
 * looking them up itself.
 */
export async function startBatch({
  formulaId,
  variantId,
  madeOn,
  containers,
  netFill,
  overagePct,
  plannedG,
  resolvedLines,
  methodSnapshot,
  status = 'in_progress',
}) {
  const { data, error } = await supabase
    .from('formula_batches')
    .insert({
      formula_id: formulaId,
      variant_id: variantId || null,
      made_on: madeOn,
      containers,
      net_fill_g: netFill,
      overage_pct: overagePct,
      planned_batch_g: plannedG,
      target_batch_g: plannedG,
      resolved_lines: resolvedLines,
      method_snapshot: methodSnapshot,
      status,
      date_started: status === 'in_progress' ? new Date().toISOString() : null,
      line_progress: [],
      step_progress: [],
    })
    .select(BATCH_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function updateBatchProgress(id, { lineProgress, stepProgress }) {
  const updates = {};
  if (lineProgress !== undefined) updates.line_progress = lineProgress;
  if (stepProgress !== undefined) updates.step_progress = stepProgress;
  const { error } = await supabase.from('formula_batches').update(updates).eq('id', id);
  if (error) throw error;
}

export async function setBatchStatus(id, status) {
  const updates = { status };
  if (status === 'in_progress') updates.date_started = new Date().toISOString();
  const { data, error } = await supabase
    .from('formula_batches')
    .update(updates)
    .eq('id', id)
    .select(BATCH_SELECT)
    .single();
  if (error) throw error;
  return data;
}

/* Finish a batch.
 *
 * Expiry is computed from the completion date and the product type's shelf
 * life rather than typed — it goes on the label, and a hand-typed date is
 * one that eventually gets typed wrong.
 */
export async function completeBatch(batch, { dateCompleted, actualUnits, actualG, notes }) {
  const months = batch.formula?.family?.product_type?.shelf_life_months;
  const { data, error } = await supabase
    .from('formula_batches')
    .update({
      status: 'completed',
      date_completed: dateCompleted,
      made_on: dateCompleted,
      actual_yield_units: actualUnits === '' || actualUnits == null ? null : Number(actualUnits),
      actual_finished_weight_g: actualG === '' || actualG == null ? null : Number(actualG),
      notes: notes?.trim() || null,
      expiry_date: expiryDate(dateCompleted, months),
    })
    .eq('id', batch.id)
    .select(BATCH_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBatch(id) {
  const { error } = await supabase.from('formula_batches').delete().eq('id', id);
  if (error) throw error;
}

// ----------------------------------------------------------- lab formulas

const LAB_FORMULA_SELECT = `
  *,
  product_type:product_types(id, name, sku_code, container_presets, default_overage_pct, shelf_life_months, default_method_id),
  method:methods(id, name, steps, temperatures),
  variants:formula_variants(id, name, overrides, status, notes),
  versions:formulas(
    id, version_number, version_label, is_current, change_note, created_at, created_by,
    flex_ingredient_id,
    formula_ingredients(*, ingredient:ingredients(id, name, cost_per_kg, max_use_pct))
  )
`;

export async function listLabFormulas() {
  const { data, error } = await supabase
    .from('formula_families')
    .select(LAB_FORMULA_SELECT)
    .order('name');
  if (error) throw error;
  return data.map(shapeFormula);
}

export async function getLabFormula(id) {
  const { data, error } = await supabase
    .from('formula_families')
    .select(LAB_FORMULA_SELECT)
    .eq('id', id)
    .single();
  if (error) throw error;
  return shapeFormula(data);
}

function shapeFormula(family) {
  const versions = [...(family.versions || [])].sort((a, b) => b.version_number - a.version_number);
  return {
    ...family,
    versions,
    currentVersion: versions.find((v) => v.is_current) || versions[0] || null,
    activeVariants: (family.variants || []).filter((v) => v.status === 'active'),
  };
}

// The effective method: the formula's own, else the product type's default.
// Resolved in one place so the formula page, the batch wizard and the sheet
// can't disagree about which steps apply.
export function effectiveMethod(formula, methods = []) {
  if (formula?.method) return formula.method;
  const fallbackId = formula?.product_type?.default_method_id;
  return fallbackId ? methods.find((m) => m.id === fallbackId) || null : null;
}

export async function setFormulaMethod(formulaId, methodId) {
  const { error } = await supabase
    .from('formula_families')
    .update({ method_id: methodId, updated_at: new Date().toISOString() })
    .eq('id', formulaId);
  if (error) throw error;
}

export async function setFlexIngredient(versionId, ingredientId) {
  const { error } = await supabase
    .from('formulas')
    .update({ flex_ingredient_id: ingredientId })
    .eq('id', versionId);
  if (error) throw error;
}
